import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../components";
import DB_Status from "../../model/enum/DBStatus";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import ToastMessages from "../../model/enum/ToastMessages";
import AlertService from "../../service/AlertService";
import EmailService from "../../service/EmailService";
import SmsService from "../../service/SmsService";
import ApiService from "../../service/ApiService";
import Request from "../../model/request";
import Service from "../../model/service";
import Client from "../../model/client";
import ClientModal from "../client/ClientModal";
import DeleteModal from "../modal/DeleteModal";
import ServiceType from "../../model/serviceType";
import Status from "../../model/status";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "../../assets/admin/edit-icon.svg";
import PageResultResponse from "../../model/pageResultResponse";
import Select, { StylesConfig } from "react-select";
import {
  DarkGrey,
  LightPrimaryColor,
  PrimaryColor,
  White,
} from "../../shared/styles/colors";
import useStyles from "./styles";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

const styles: StylesConfig<any, true> = {
  container: (base) => ({
    ...base,
    width: "95% !important",
    borderColor: DarkGrey,
    borderRadius: "5px",
    marginLeft: "10px",
  }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? PrimaryColor : DarkGrey,
    boxShadow: state.isFocused ? LightPrimaryColor : base.boxShadow,
    "&:hover": {
      borderColor: PrimaryColor,
    },
    "&:selected": {
      borderColor: PrimaryColor,
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? LightPrimaryColor
      : state.isSelected
      ? PrimaryColor
      : base.backgroundColor,
    color: state.isFocused || state.isSelected ? White : base.color,
  }),
  menu: (base) => ({
    ...base,
  }),
};

const RequestModal = ({ request, resetList }: any) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const edit = request != null;
  const id = edit ? request.id : 0;
  const [client, setClient] = useState(edit ? request.Client : null);
  const [clients, setClients] = useState<Client[]>([]);
  const [serviceId, setServiceId] = useState(edit ? request.Service?.id : null);
  const [services, setServices] = useState<Service[]>([]);
  const [serviceTypeId, setServiceTypeId] = useState(
    edit ? request.ServiceType?.id : null
  );
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [description, setDescription] = useState(
    edit ? request.description : null
  );
  const [status, setStatus] = useState(edit ? request?.Status : null);
  const [statusList, setStatusList] = useState<Status[]>([]);
  const [notify, setNotify] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch all
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [clientsData, servicesData, serviceTypesData, statusData] =
          await Promise.all([
            ApiService.get<PageResultResponse>(DatabaseEntities.CLIENTS),
            ApiService.get<PageResultResponse>(DatabaseEntities.SERVICES),
            ApiService.get<ServiceType[]>(DatabaseEntities.SERVICE_TYPES),
            ApiService.get<Status[]>(`${DatabaseEntities.STATUS}?type=General`),
          ]);
        setClients(clientsData.items);
        setServices(servicesData.items);
        setServiceTypes(serviceTypesData);
        setStatusList(statusData);
      } catch (error) {
        AlertService.error(ToastMessages.FETCH_FAILED);
      } finally {
        setLoading(false);
      }
    };
    if (open) fetchData();
  }, [open]);

  const handleClient = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setClient(null);
      return;
    }

    // Otherwise, e is an object with value and label properties
    let value = e.value;
    var clt = clients.find((x) => x.id == value);
    setClient(clt);
  };

  const handleService = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setServiceId(null);
      return;
    }

    setServiceId(e.value);
  };

  const handleServiceType = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setServiceTypeId(null);
      return;
    }

    setServiceTypeId(e.value);
  };

  const handleDescription = (e: any) => setDescription(e.target.value);

  const handleStatus = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setStatus(null);
      return;
    }

    let value = e.value;
    var stt = statusList.find((x) => x.id == value);
    setStatus(stt);
  };

  const handleNotification = () => setNotify((notify) => !notify);

  const handleCreate = async (payload: any) => {
    await ApiService.post<Request>(DatabaseEntities.REQUESTS, payload)
      .then(() => {
        AlertService.success(ToastMessages.CREATED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.CREATE_FAILED);
      });
  };

  const handleUpdate = async (payload: any) => {
    await ApiService.put<Request>(
      `${DatabaseEntities.REQUESTS}/${payload.id}`,
      payload
    )
      .then(() => {
        AlertService.success(ToastMessages.UPDATED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: any) => {
    await ApiService.delete<any>(`${DatabaseEntities.REQUESTS}/${payload.id}`)
      .then(() => {
        AlertService.success(ToastMessages.DELETED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const register = async () => {
    if (invalidInput()) return;

    const payload: Request = {
      id: id,
      serviceId: serviceId,
      serviceTypeId: serviceTypeId,
      description: description,
      clientId: client.id,
      statusId: status.id,
    };

    if (edit) {
      await handleUpdate(payload)
        .then(async () => {
          if (notify) {
            await handleEmailAndSmsSend();
          }
        })
        .catch(() => {
          AlertService.error(ToastMessages.REQUEST_FAILED);
        });
    } else {
      await handleCreate(payload)
        .then(async () => {
          if (notify) {
            await handleEmailAndSmsSend();
          }
        })
        .catch(() => {
          AlertService.error(ToastMessages.REQUEST_FAILED);
        });
    }
    handleClose();
  };

  const handleEmailAndSmsSend = async () => {
    let type: string = "";

    switch (status.name) {
      case DB_Status.ACCEPTED:
        type = "Request Accepted";
        break;
      case DB_Status.CANCELED:
        type = "Request Denied";
        break;
      case DB_Status.COMPLETED:
        type = "Service Completed";
        break;
      default:
        break;
    }

    if (type != "") {
      await SmsService.send(client.phone, type)
        .then(() => {
          AlertService.success("Sms notification sent");
        })
        .catch(() => {
          AlertService.error("Sms notification not sent");
        });
      await EmailService.sendEmailToUser(client.firstName, client.email, type)
        .then(() => {
          AlertService.success("Email notification sent");
        })
        .catch(() => {
          AlertService.error("Email notification not sent");
        });
    }
  };

  const invalidInput = () => {
    return (
      !serviceTypeId ||
      !serviceId ||
      !description ||
      !status ||
      !client ||
      !client.id
    );
  };

  return (
    <div>
      {edit ? (
        <Button onClick={handleOpen}>
          <img src={EditIcon} alt="edit icon" />
        </Button>
      ) : (
        <Btn
          className={classes.headerButton}
          variant="contained"
          onClick={handleOpen}
        >
          + Add Request
        </Btn>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Box sx={style}>
            <div className={classes.headerDiv}>
              {edit ? (
                <DeleteModal item={request} handleDelete={handleDelete} />
              ) : (
                <Typography className={classes.title}>Request quote</Typography>
              )}
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
            <hr />
            <div className={classes.form}>
              <div className={classes.inputGroup}>
                <Select
                  styles={styles}
                  value={
                    client?.id
                      ? {
                          value: client.id,
                          label: `${client.firstName} ${client.lastName}`,
                        }
                      : ""
                  }
                  isClearable={true}
                  onChange={handleClient}
                  options={clients.map((clt: Client) => ({
                    value: clt.id,
                    label: `${clt.firstName} ${clt.lastName}`,
                  }))}
                  placeholder="Select Client"
                />
                <ClientModal
                  externalService={true}
                  resetList={() => setClients([])}
                  setClient={(clt: Client) => setClient(clt)}
                />
              </div>
              <div className={classes.inputGroup}>
                <Select
                  styles={styles}
                  value={
                    serviceId
                      ? {
                          value: serviceId,
                          label: `${
                            services.find((s) => s.id === serviceId)?.name
                          }`,
                        }
                      : ""
                  }
                  isClearable={true}
                  onChange={handleService}
                  options={services.map((service: Service) => ({
                    value: service.id,
                    label: `${service.name}`,
                  }))}
                  placeholder="Select a service"
                />
                <Select
                  styles={styles}
                  value={
                    serviceTypeId
                      ? {
                          value: serviceTypeId,
                          label: `${
                            serviceTypes.find((s) => s.id === serviceTypeId)
                              ?.name
                          }`,
                        }
                      : ""
                  }
                  isClearable={true}
                  onChange={handleServiceType}
                  options={serviceTypes.map((serviceType: ServiceType) => ({
                    value: serviceType.id,
                    label: `${serviceType.name}`,
                  }))}
                  placeholder="Select a project"
                />
              </div>
              <textarea
                className={classes.textarea}
                name="description"
                placeholder="Description..."
                onChange={handleDescription}
                defaultValue={description}
              />
              <Select
                styles={styles}
                value={
                  status?.id
                    ? {
                        value: status.id,
                        label: `${status.name}`,
                      }
                    : ""
                }
                isClearable={true}
                onChange={handleStatus}
                options={statusList.map((stts: Status) => ({
                  value: stts.id,
                  label: `${stts.name}`,
                }))}
                placeholder="Select Status"
              />
              <div className={classes.inputGroup}>
                <input
                  type="checkbox"
                  className={classes.checkbox}
                  onChange={handleNotification}
                  checked={notify}
                />
                <Typography className={classes.checkboxtext}>
                  Send notification{" "}
                </Typography>
              </div>
            </div>
            <div className={classes.buttonDiv}>
              <Btn
                className={classes.button}
                onClick={() => register()}
                disabled={invalidInput()}
              >
                {edit ? "Save" : "Create"}
              </Btn>
            </div>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default RequestModal;
